"use client";

/**
 * Accessible video modal. APG dialog behavior: role="dialog" + aria-modal,
 * labelled by the video title, focus trapped inside, Escape and the close
 * button (and a backdrop click — closing only pauses playback, no state is
 * lost) close it, focus returns to the opener, background scroll is locked.
 * The player is mounted only while open, so closing always stops audio and
 * nothing downloads before the visitor asked to watch.
 */

import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { useT } from "@/i18n";
import type { MarketingVideo } from "@/lib/marketing-videos";
import { trackVideo } from "./video-analytics";
import { VideoPlayer } from "./video-player";

export function VideoModal({
  video,
  title,
  open,
  onClose,
}: {
  video: MarketingVideo;
  /** Localized, visible modal title. */
  title: string;
  open: boolean;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const t = useT();

  useEffect(() => {
    if (!open) return;
    trackVideo(video.analyticsName, "modal_open");
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      // Focus trap over the dialog's focusable elements.
      const root = dialogRef.current;
      if (!root) return;
      const focusables = Array.from(
        root.querySelectorAll<HTMLElement>(
          'button, [href], video, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute("disabled"));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !root.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.overflow = prevOverflow;
      trackVideo(video.analyticsName, "modal_close");
      previouslyFocused?.focus?.();
    };
  }, [open, onClose, video.analyticsName]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div aria-hidden className="absolute inset-0 bg-bg-base/90 backdrop-blur-sm" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative w-full max-w-4xl rounded-2xl border border-line-subtle bg-bg-raised p-3 shadow-2xl md:p-4"
      >
        <div className="mb-2 flex items-center justify-between gap-4 px-1">
          <h2 className="truncate text-[14px] font-semibold text-fg-bright md:text-[15px]">
            {title}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={t("videoKit.closeLabel")}
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-line-subtle bg-bg-elev text-fg-dim transition hover:bg-bg-hover hover:text-fg-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          >
            <X size={17} />
          </button>
        </div>
        <VideoPlayer video={video} autoPlay />
      </div>
    </div>
  );
}
